"use client";

import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { Modal, ProgressBar, Text, useDebug } from "@huub-web/ui";
import {
  addParamsToURL,
  getCookie,
  deleteCookie,
  localStore,
  sessionStore,
} from "@huub-web/utils";

import {
  AuthContext,
  type AuthContextState,
  type AuthUnauthorizedType,
} from "../../contexts";
import type {
  AuthIdentityModel,
  JWTIdentity,
  ProviderMismatchError,
} from "../../types";

/**
 * Storage key for the app session id_token.
 *
 * Held in `sessionStorage` (not `localStorage`) so the session credential is
 * tab-scoped and evicted on tab close instead of persisting indefinitely,
 * shrinking the XSS at-rest blast radius (SMA-2668). Full migration to an
 * in-memory + httpOnly-refresh-cookie model is a backend-coordinated follow-up.
 */
export const IDENTITY_TOKEN_KEY = "identityToken";

/**
 * Cookie name for cross-domain auth token.
 * Must match the cookie name used in marketing site.
 */
export const AUTH_COOKIE_NAME = "huub_id_token";

/**
 * Reads the app session token, migrating any legacy `localStorage` value.
 *
 * Before SMA-2668 the id_token lived in `localStorage`. Already-authenticated
 * browsers still carry that long-lived copy, so on init we prefer the
 * `sessionStorage` value but fall back to the legacy `localStorage` entry,
 * copy it into `sessionStorage`, and delete the `localStorage` copy so no
 * token lingers at rest.
 * @returns The current session token, or `null` when none exists
 */
const readAndMigrateIdentityToken = (): string | null => {
  const sessionToken = sessionStore.get(IDENTITY_TOKEN_KEY);
  if (sessionToken) {
    // A newer session token already exists; drop any stale legacy copy.
    localStore.remove(IDENTITY_TOKEN_KEY);
    return sessionToken;
  }

  const legacyToken = localStore.get(IDENTITY_TOKEN_KEY);
  if (legacyToken) {
    // Only drop the durable legacy copy once the session write has actually
    // succeeded — otherwise a failed sessionStorage write (quota / disabled)
    // would leave no durable token and log the user out on reload.
    if (sessionStore.set(IDENTITY_TOKEN_KEY, legacyToken)) {
      localStore.remove(IDENTITY_TOKEN_KEY);
    }
    return legacyToken;
  }

  return null;
};

/**
 * Removes the id_token from both session and legacy `localStorage` so no
 * credential lingers at rest after clear/logout/unauthorized.
 */
const clearStoredIdentityToken = (): void => {
  sessionStore.remove(IDENTITY_TOKEN_KEY);
  localStore.remove(IDENTITY_TOKEN_KEY);
};

export type AuthProviderProps = {
  /**
   * Callback when user is unauthorized (missing or invalid token)
   */
  onUnauthorized?: (type: AuthUnauthorizedType) => void;
  /**
   * Callback when logout completes (with optional failure flag)
   */
  onLogout?: (failed?: boolean) => void;
  /**
   * Callback when a provider mismatch is detected.
   * Called after tokens are cleared, before redirect to login.
   */
  onProviderMismatch?: (error: ProviderMismatchError) => void;
  /**
   * URL to marketing login page (for redirects)
   */
  loginURL: string;
  /**
   * URL to marketing registration page
   */
  // registerURL: string;
  /**
   * Whether to clear the token on mount
   */
  clearToken?: boolean;
  /**
   * Backend logout endpoint URL
   */
  logoutEndpoint: string;
  /**
   * Backend token refresh endpoint URL
   */
  refreshEndpoint: string;
  /**
   * Backend resend verification email endpoint URL
   */
  // resendVerificationEmailEndpoint?: string;
  /**
   * Cookie domain for clearing auth cookie (e.g., ".huub.nl").
   * Leave empty for localhost.
   */
  authCookieDomain?: string;
  /**
   * Cookie name for cross-domain auth token.
   * Use environment-specific names to prevent cross-environment token usage.
   * E.g., "huub_id_token_testing", "huub_id_token_acceptance", "huub_id_token".
   */
  authCookieName?: string;
  /**
   * Child components
   */
  children: ReactNode;
};

/**
 * Provides authentication context to the application.
 * @todo Auth has been moved to the marketing site, this provider need refac
 */
export const AuthProvider: FC<AuthProviderProps> = ({
  onUnauthorized,
  onLogout,
  onProviderMismatch,
  loginURL,
  clearToken = false,
  logoutEndpoint,
  refreshEndpoint,
  authCookieDomain,
  authCookieName = AUTH_COOKIE_NAME,
  children,
}) => {
  const { t } = useTranslation();
  const { log } = useDebug();

  // Initialize token by checking cookie first (new token from marketing site),
  // then fall back to session storage (existing session in this tab).
  // This prevents "session expired" errors when user just logged in via marketing.
  const [identityToken, setIdentityToken] = useState<string | null>(() => {
    if (clearToken) {
      return null;
    }

    // Check cookie first - new token from marketing site takes priority
    const cookieToken = getCookie(authCookieName);
    if (cookieToken) {
      // Transfer cookie to session storage immediately. Only delete the
      // one-time cookie once the session write has actually succeeded —
      // otherwise a failed sessionStorage write (quota / disabled) would
      // discard both durable copies and log the user out on reload.
      if (sessionStore.set(IDENTITY_TOKEN_KEY, cookieToken)) {
        deleteCookie(authCookieName, {
          domain: authCookieDomain || undefined,
          path: "/",
        });
      }
      return cookieToken;
    }

    // Fall back to the session-storage token, migrating any legacy
    // localStorage token forward so existing sessions survive the upgrade.
    return readAndMigrateIdentityToken();
  });

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // When the user becomes unauthorized we trigger a redirect to the login
  // page (via onUnauthorized). The redirect isn't instant, so we flag it here
  // to keep the app in a loading state instead of flashing an error screen
  // while the browser navigates away.
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  // Use a ref for isRefreshing to avoid stale closures in async callbacks.
  // When API requests check this value during 401 handling, they need the
  // current value, not the value captured when the callback was created.
  const isRefreshingRef = useRef(false);

  const forcedLoginURL = addParamsToURL(loginURL, { prompt: "true" });

  // Clear token
  // ------------------------------

  const isTokenClearedRef = useRef(false);

  useEffect(() => {
    if (clearToken) {
      if (isTokenClearedRef.current) {
        return;
      }

      isTokenClearedRef.current = true;
      log("blue", "AuthProvider", "🛡️ Clearing token");
      // Clear token state and both session + legacy localStorage synchronously
      // to prevent race conditions and leave nothing at rest.
      setIdentityToken(null);
      clearStoredIdentityToken();
    } else {
      // Reset the flag when clearToken becomes false
      isTokenClearedRef.current = false;
    }
  }, [clearToken]);

  // Initialize middleware
  // ------------------------------

  const initializeMiddleware = () => {
    // Skip initialization if we're in the process of clearing the token
    if (clearToken) {
      log(
        "blue",
        "AuthProvider",
        "🛡️ Skipping initialization - token is being cleared",
      );
      return;
    }

    log("blue", "AuthProvider", "🛡️ Initializing auth provider");

    // Token was already loaded from cookie or session storage in useState initializer.
    // Here we just need to decode it, set identity, and check expiration.
    const token = identityToken ?? readAndMigrateIdentityToken();

    if (token) {
      try {
        const decoded = jwtDecode<JWTIdentity>(token);
        setIdentity(decoded);

        if (isTokenExpired(decoded)) {
          log("blue", "AuthProvider", "🛡️ Token expired, refreshing");
          refreshToken();
        } else {
          log("blue", "AuthProvider", "🛡️ Token valid");
          setIdentityToken(token);
        }
      } catch (e) {
        console.error("Failed to decode identity token", e);
        unauthorized("invalid");
      }
    } else {
      unauthorized("missing");
    }
  };

  // Identity
  // ------------------------------

  const [indentity, setIndentityModel] = useState<AuthIdentityModel | null>(
    null,
  );

  const setIdentity = (identity: JWTIdentity) => {
    const user: AuthIdentityModel = {
      familyName: identity?.family_name ?? "",
      givenName: identity?.given_name ?? "",
      name: identity?.name ?? "",
      picture: identity.picture,
      email: identity.email,
    };

    setIndentityModel(user);
  };

  const unauthorized: AuthContextState["unauthorized"] = (type) => {
    log(
      "blue",
      "AuthProvider",
      `Unauthorized, clearing identity token (${type})`,
    );

    setIdentityToken(null);
    clearStoredIdentityToken();
    setIsUnauthorized(true);
    onUnauthorized?.(type);
  };

  // Core Logout Logic
  // ------------------------------

  type PerformLogoutOptions = {
    /** Reason for the logout, used for logging and conditional callbacks */
    reason?: "user_initiated" | "provider_mismatch";
    /** Provider mismatch error details, required when reason is "provider_mismatch" */
    providerMismatchError?: ProviderMismatchError;
  };

  /**
   * Shared logout logic: clears auth state, cookie, calls backend logout,
   * and triggers the appropriate callbacks.
   */
  const performLogout = async ({
    reason = "user_initiated",
    providerMismatchError,
  }: PerformLogoutOptions = {}) => {
    // Clear local auth state (session + legacy localStorage)
    setIdentityToken(null);
    clearStoredIdentityToken();

    // Clear the cross-domain auth cookie
    deleteCookie(authCookieName, {
      domain: authCookieDomain || undefined,
      path: "/",
    });

    // Notify the consuming app of provider mismatch if applicable
    if (reason === "provider_mismatch" && providerMismatchError) {
      onProviderMismatch?.(providerMismatchError);
    }

    try {
      const response = await fetch(logoutEndpoint, {
        method: "GET",
        headers: { "X-Id-Token": `${identityToken}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      onLogout?.();
    } catch (e) {
      console.error("Failed to logout", e);
      onLogout?.(true);
    }
  };

  // Logout
  // ------------------------------

  const logout = async () => {
    setIsLoggingIn(false);
    setIsLoggingOut(true);
    await performLogout({ reason: "user_initiated" });
  };

  // Provider Mismatch
  // ------------------------------

  // Use a ref to prevent multiple simultaneous provider mismatch logouts
  const isHandlingProviderMismatchRef = useRef(false);

  const handleProviderMismatch = async (error: ProviderMismatchError) => {
    // Prevent multiple simultaneous provider mismatch handling
    if (isHandlingProviderMismatchRef.current) {
      log(
        "blue",
        "AuthProvider",
        "🛡️ Provider mismatch already being handled, skipping",
      );
      return;
    }

    try {
      isHandlingProviderMismatchRef.current = true;

      log(
        "blue",
        "AuthProvider",
        `🛡️ Provider mismatch detected: ${error.message}`,
      );

      await performLogout({
        reason: "provider_mismatch",
        providerMismatchError: error,
      });
    } finally {
      isHandlingProviderMismatchRef.current = false;
    }
  };

  // Refreshing token
  // ------------------------------

  const isTokenExpired = (identity: JWTIdentity): boolean => {
    const expiration = new Date((identity.exp ?? 0) * 1000);
    const now = new Date();

    if (expiration < now) {
      log(
        "blue",
        "AuthProvider",
        `Identity token expired, refreshing (${expiration.toUTCString()} < ${now.toUTCString()})`,
      );

      return true;
    }

    log(
      "blue",
      "AuthProvider",
      `Identity valid (${expiration.toUTCString()} < ${now.toUTCString()})`,
    );

    return false;
  };

  const refreshToken = async () => {
    log("blue", "AuthProvider", "🛡️ Starting token refresh");
    isRefreshingRef.current = true;

    try {
      const response = await fetch(refreshEndpoint, {
        method: "GET",
        headers: { "X-Id-Token": `${identityToken}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as { id_token: string };
      const { id_token: token } = data;

      try {
        const decoded = jwtDecode<JWTIdentity>(token);
        sessionStore.set(IDENTITY_TOKEN_KEY, token);
        setIdentityToken(token);
        setIdentity(decoded);

        log("blue", "AuthProvider", "🛡️ Token refreshed successfully");
      } catch (e) {
        console.error("Failed to decode identity token", e);
        unauthorized("invalid");
      }
    } catch (e) {
      console.error("Failed to refresh identity token", e);
      unauthorized("invalid");
    } finally {
      isRefreshingRef.current = false;
      log("blue", "AuthProvider", "🛡️ Token refresh completed");
    }
  };

  // Resending verification email
  // ------------------------------
  // Note: Email verification is now handled by marketing site.
  // These are kept for context API compatibility but are no-ops.

  // const resendVerificationEmail = async (
  //   _onSuccess?: () => void,
  //   onError?: (error: string, code: AuthErrorCode) => void,
  // ) => {
  //   // Email verification is handled by marketing site
  //   console.warn(
  //     "resendVerificationEmail is deprecated - use marketing site for email verification",
  //   );
  //   onError?.(
  //     "Email verification is handled by marketing site",
  //     AUTH_ERROR_CODES.VERIFICATION_EMAIL_NO_AUTH_ID,
  //   );
  // };

  return (
    <AuthContext.Provider
      value={{
        loginURL,
        forcedLoginURL,
        identityToken,
        indentity,
        isRefreshingRef,
        isUnauthorized,
        unauthorized,
        initializeMiddleware,
        logout,
        handleProviderMismatch,
      }}
    >
      {children}

      {isLoggingIn && (
        <Modal title={t("auth.modal.login.title")} placement="center">
          <ProgressBar variant="infinite" position="absolute" />
          <Text markdown>{t("auth.modal.login.body")}</Text>
        </Modal>
      )}

      {isLoggingOut && (
        <Modal title={t("auth.modal.logout.title")} placement="center">
          <ProgressBar variant="infinite" position="absolute" />
          <Text markdown>{t("auth.modal.logout.body")}</Text>
        </Modal>
      )}
    </AuthContext.Provider>
  );
};
